import Config from '@/Config';

/**
 * Checks if Transcend integration is enabled
 * @returns {boolean} True if Transcend is enabled
 */
export function isTranscendEnabled() {
    return Config.siteFeatures.transcendIntegrationEnabled;
}

/**
 * Gets the current advertising consent status from Transcend
 * @returns {boolean} True if advertising consent is granted
 */
export function getAdvertisingConsent() {
    if (!isTranscendEnabled() || !window.airgap) {
        return true; // Default to true when Transcend is disabled
    }

    try {
        const consent = window.airgap.getConsent();
        return consent?.purposes?.Advertising === true;
    }
    catch (error) {
        console.error('[Privacy] Error getting consent:', error);
        return false; // Default to false on error when Transcend is enabled
    }
}

/**
 * Attaches a listener for Transcend consent changes
 * @param {Function} callback - Callback function that receives the consent object
 * @returns {Function|null} Cleanup function to remove the listener, or null
 */
export function attachConsentChangeListener(callback) {
    if (!isTranscendEnabled() || !window.airgap) {
        return null;
    }

    let cleanupFn = null;

    window.airgap.ready((airgap) => {
        // Get initial consent state
        try {
            const initialConsent = airgap.getConsent();
            callback(initialConsent);
        }
        catch (error) {
            console.error('[Privacy] Error getting initial consent:', error);
        }

        // Listen for consent changes
        const listener = ({ detail: { consent } }) => {
            callback(consent);
        };

        airgap.addEventListener('consent-change', listener);

        // Return cleanup function
        cleanupFn = () => {
            airgap.removeEventListener('consent-change', listener);
        };
    });

    return cleanupFn;
}

/**
 * Opens the Transcend consent manager dialog
 */
export function openConsentManager() {
    if (window.transcend) {
        window.transcend.showConsentManager();
    }
    else {
        console.warn('[Privacy] Transcend consent manager not available');
    }
}

/**
 * Checks if content should be blocked based on Transcend consent
 * @param {boolean} isVideoEmbed - Whether the content is a video embed (YouTube, Vimeo only)
 * @param {boolean} isAdvertisingConsentGranted - Current advertising consent status
 * @returns {boolean} True if content should be blocked
 */
export function shouldBlockContent(isVideoEmbed, isAdvertisingConsentGranted) {
    // Don't block if Transcend is disabled
    if (!isTranscendEnabled()) {
        return false;
    }

    // Don't block video embeds (YouTube, Vimeo only - NOT Twitch)
    if (isVideoEmbed) {
        return false;
    }

    // Block if advertising consent is not granted
    return !isAdvertisingConsentGranted;
}
